<template>
  <div class="space-y-6">
    <!-- Header -->
    <div class="bg-card rounded-lg border p-6">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-lg font-semibold">Symbol Blacklist</h3>
          <p class="text-sm text-muted-foreground">
            Manage symbols to exclude from analysis
          </p>
        </div>
        <div class="text-right">
          <p class="text-2xl font-bold">{{ blacklistData.count }}</p>
          <p class="text-sm text-muted-foreground">Blacklisted</p>
        </div>
      </div>
    </div>

    <!-- Add Symbol Form -->
    <div class="bg-card rounded-lg border p-6">
      <h4 class="text-md font-semibold mb-4">Add Symbol to Blacklist</h4>
      <form @submit.prevent="addSymbol" class="flex gap-4">
        <div class="flex-1">
          <SymbolSelector
            v-model="newSymbol"
            placeholder="Search for a symbol to blacklist..."
          />
        </div>
        <button
          type="submit"
          :disabled="isAdding || !newSymbol"
          class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4"
        >
          <svg
            v-if="isAdding"
            class="mr-2 h-4 w-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          {{ isAdding ? 'Adding...' : 'Add to Blacklist' }}
        </button>
      </form>
    </div>

    <!-- Blacklist Table -->
    <div class="bg-card rounded-lg border">
      <div class="p-6 border-b">
        <h3 class="text-lg font-semibold">Blacklisted Symbols</h3>
      </div>

      <div v-if="isLoading" class="p-6 text-center">
        <svg
          class="mx-auto h-8 w-8 animate-spin text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        <p class="mt-2 text-sm text-muted-foreground">Loading blacklist...</p>
      </div>

      <div v-else-if="blacklistData.symbols.length === 0" class="p-6 text-center">
        <div
          class="h-12 w-12 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center"
        >
          <svg
            class="h-6 w-6 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
        </div>
        <p class="text-sm text-muted-foreground">
          No symbols blacklisted. Add symbols above to exclude them from analysis.
        </p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="border-b bg-muted/50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Symbol
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr
              v-for="symbol in blacklistData.symbols"
              :key="symbol"
              class="hover:bg-muted/50"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <span class="text-sm font-medium">{{ symbol }}</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <button
                  @click="removeSymbol(symbol)"
                  :disabled="isRemoving"
                  class="text-red-600 hover:text-red-800 disabled:opacity-50"
                >
                  Remove
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { apiClient, type BlacklistResponse } from '@/lib/api'
import SymbolSelector from '@/components/SymbolSelector.vue'

// Reactive data
const isLoading = ref(false)
const isAdding = ref(false)
const isRemoving = ref(false)
const blacklistData = ref<BlacklistResponse>({
  symbols: [],
  count: 0
})
const newSymbol = ref('')

// Methods
const loadBlacklist = async () => {
  isLoading.value = true
  try {
    blacklistData.value = await apiClient.getBlacklist()
  } catch (error) {
    console.error('Failed to load blacklist:', error)
  } finally {
    isLoading.value = false
  }
}

const addSymbol = async () => {
  if (!newSymbol.value.trim()) return

  isAdding.value = true
  try {
    await apiClient.addToBlacklist(newSymbol.value.trim())
    newSymbol.value = ''
    await loadBlacklist()
  } catch (error: any) {
    console.error('Failed to add symbol:', error)
    if (error.response?.data?.detail) {
      alert(error.response.data.detail)
    }
  } finally {
    isAdding.value = false
  }
}

const removeSymbol = async (symbol: string) => {
  if (!confirm(`Are you sure you want to remove ${symbol} from the blacklist?`)) {
    return
  }

  isRemoving.value = true
  try {
    await apiClient.removeFromBlacklist(symbol)
    await loadBlacklist()
  } catch (error) {
    console.error('Failed to remove symbol:', error)
  } finally {
    isRemoving.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadBlacklist()
})
</script>
